import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Calendar, User, ArrowLeft, ArrowRight, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = ['all', 'Premium Financing', 'Education', 'Family Planning'];

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      // Fallback data
      setPosts([
        {
          id: '1',
          title: 'Understanding Premium Financing for High-Net-Worth Families',
          slug: 'understanding-premium-financing',
          excerpt: 'Learn how premium financing can help preserve capital while securing substantial life insurance coverage.',
          category: 'Premium Financing',
          author: 'Insubazaar Team',
          image_url: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800',
          created_at: new Date().toISOString()
        },
        {
          id: '2',
          title: 'Why Insurance Carrier Ratings Matter',
          slug: 'insurance-carrier-ratings-matter',
          excerpt: 'Discover why choosing an A+ or A++ rated insurance carrier is crucial for long-term financial security.',
          category: 'Education',
          author: 'Insubazaar Team',
          image_url: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800',
          created_at: new Date().toISOString()
        },
        {
          id: '3',
          title: 'Life Insurance for South Asian Families: A Cultural Perspective',
          slug: 'life-insurance-south-asian-families',
          excerpt: 'How life insurance fits into the unique financial planning needs of South Asian families.',
          category: 'Family Planning',
          author: 'Insubazaar Team',
          image_url: 'https://images.unsplash.com/photo-1511895426328-dc8714191300?w=800',
          created_at: new Date().toISOString()
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const filteredPosts = selectedCategory === 'all' 
    ? posts 
    : posts.filter(post => post.category === selectedCategory);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="bg-[#020617] min-h-screen pt-20" data-testid="blog-page">
      {/* Hero Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="text-[#C5A059] text-sm uppercase tracking-widest mb-4 block">
              Insights & Articles
            </span>
            <h1 className="font-serif text-4xl md:text-5xl text-white leading-tight mb-6">
              The ShopCompareInsure Blog
            </h1>
            <p className="text-slate-300 text-xl leading-relaxed">
              Expert insights on life insurance, premium financing, and wealth planning for global families.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="border-y border-slate-800/50 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center space-x-1 overflow-x-auto py-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 text-sm uppercase tracking-wider whitespace-nowrap transition-colors ${
                  selectedCategory === category
                    ? 'text-[#C5A059] bg-[#C5A059]/10'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-48 bg-slate-800 rounded-lg mb-4"></div>
                  <div className="h-4 bg-slate-800 rounded w-1/4 mb-2"></div>
                  <div className="h-6 bg-slate-800 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-slate-800 rounded w-full"></div>
                </div>
              ))}
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-slate-400">No articles found in this category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <article
                  key={post.id}
                  className="group bg-slate-900/30 border border-slate-800 rounded-lg overflow-hidden hover:border-[#C5A059]/30 transition-all"
                  data-testid={`blog-post-${post.slug}`}
                >
                  <Link to={`/blog/${post.slug}`}>
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={post.image_url || 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800'}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <span className="text-[#C5A059] text-xs uppercase tracking-wider">
                        {post.category}
                      </span>
                      <h2 className="font-serif text-xl text-white mt-2 mb-3 group-hover:text-[#C5A059] transition-colors line-clamp-2">
                        {post.title}
                      </h2>
                      <p className="text-slate-400 text-sm leading-relaxed line-clamp-3 mb-4">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-slate-500">
                        <div className="flex items-center space-x-2">
                          <User className="w-3 h-3" />
                          <span>{post.author}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Calendar className="w-3 h-3" />
                          <span>{formatDate(post.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-3xl text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
            Compare quotes from top-rated carriers and speak with our specialists.
          </p>
          <Link
            to="/compare"
            className="inline-flex items-center justify-center bg-[#C5A059] text-[#020617] hover:bg-[#D4AF37] font-semibold px-8 py-4 uppercase tracking-wider text-sm transition-all group"
          >
            Compare Life Insurance Quotes
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
};

// Blog Post Detail Page
export const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/blog/${slug}`);
      setPost(response.data);
    } catch (error) {
      console.error('Error fetching blog post:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="bg-[#020617] min-h-screen pt-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-24">
          <div className="animate-pulse">
            <div className="h-8 bg-slate-800 rounded w-3/4 mb-4"></div>
            <div className="h-4 bg-slate-800 rounded w-1/2 mb-8"></div>
            <div className="h-64 bg-slate-800 rounded mb-8"></div>
            <div className="space-y-3">
              <div className="h-4 bg-slate-800 rounded"></div>
              <div className="h-4 bg-slate-800 rounded"></div>
              <div className="h-4 bg-slate-800 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="bg-[#020617] min-h-screen pt-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-24 text-center">
          <h1 className="font-serif text-3xl text-white mb-4">Article Not Found</h1>
          <p className="text-slate-400 mb-8">The article you're looking for doesn't exist.</p>
          <Link
            to="/blog"
            className="inline-flex items-center text-[#C5A059] hover:text-[#D4AF37]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#020617] min-h-screen pt-20" data-testid="blog-post-detail">
      <article className="max-w-3xl mx-auto px-6 md:px-12 py-24">
        {/* Back Link */}
        <Link
          to="/blog"
          className="inline-flex items-center text-slate-400 hover:text-[#C5A059] mb-8 text-sm"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Blog
        </Link>

        {/* Header */}
        <header className="mb-8">
          <span className="text-[#C5A059] text-sm uppercase tracking-wider">
            {post.category}
          </span>
          <h1 className="font-serif text-3xl md:text-4xl text-white mt-2 mb-4 leading-tight">
            {post.title}
          </h1>
          <div className="flex items-center space-x-4 text-sm text-slate-500">
            <div className="flex items-center space-x-2">
              <User className="w-4 h-4" />
              <span>{post.author}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(post.created_at)}</span>
            </div>
          </div>
        </header>

        {/* Featured Image */}
        {post.image_url && (
          <img
            src={post.image_url}
            alt={post.title}
            className="w-full rounded-lg mb-8"
          />
        )}

        {/* Content */}
        <div className="prose prose-invert prose-slate max-w-none">
          <div className="text-slate-300 leading-relaxed whitespace-pre-wrap">
            {post.content}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 p-8 bg-slate-900/50 border border-slate-800 rounded-lg text-center">
          <h3 className="font-serif text-xl text-white mb-3">
            Ready to explore your options?
          </h3>
          <p className="text-slate-400 mb-6 text-sm">
            Compare quotes from top-rated carriers in minutes.
          </p>
          <Link
            to="/compare"
            className="inline-flex items-center justify-center bg-[#C5A059] text-[#020617] hover:bg-[#D4AF37] font-semibold px-6 py-3 uppercase tracking-wider text-xs transition-all"
          >
            Get a Quote
          </Link>
        </div>
      </article>
    </div>
  );
};

export default BlogPage;
